import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { Property, MapboxFeature } from '../types';
import mapboxgl from 'mapbox-gl';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiY292ZXJ6b25lIiwiYSI6ImNtMG03eDYxODA2YTYya3NheW4wM25nbjcifQ.zZ6gyVv7yu7kF-gmWRL-Qw';

const API_URL = process.env.REACT_APP_API_URL || '';

// Options arrays (exactly as in the modal)
const propertyTypes = ['House', 'Townhouse', 'Cottage (Attached)', 'Cottage (Not Attached)', 'Cluster', 'Flat Above Ground', 'Flat On Ground', 'Outbuilding', 'Storage Facility', 'Park Home/Caravan', 'Plot', 'Established', 'New Extension', 'Farm', 'Informal Settlement', 'Other'];
const residenceTypes = ['Primary Residence', 'Business Residence', 'Holiday Residence', 'Other'];
const situatedAtOptions = ['Plot', 'New Extension', 'Farm', 'Established', 'InformalSettlement', 'Rural Area', 'Suburb', 'Other'];
const coverTypeOptions = ['Comprehensive', 'Theft Cover Excluding', 'Flood Cover Excluding', 'Advensure'];
const buildingAgeOptions = ['0-5 years', '6-10 years', '11-25 years', 'Older than 25 years'];
const wallMaterialsOptions = ['Brick', 'Cement', 'Corrugated Iron', 'Glass', 'Prefab', 'Asbestos', 'Wood', 'Other'];
const roofMaterialsOptions = ['Concrete', 'Corrugated Iron', 'Glass', 'Thatch', 'Asbestos', 'Wood', 'Tiles'];
const floorMaterialsOptions = ['Carpet', 'Sealed', 'Tiles', 'Wooden Floors'];
const surroundingAreasOptions = ['Farm', 'Plot', 'Golf Estate', 'Country Estate/Residential Area', 'Security Complex', 'Complex With No Access Control', 'Boomed Off Suburb', 'Suburb', 'Industrial Area', 'Commercial Area', 'Inner City/CBD', 'Informal Settlement', 'Retirement Village', 'Nature Reserve', 'Holiday Resort', 'School/University', 'Parks', 'Highways', 'Railway Lines', 'Shops/Cafes', 'Squatter Camps', 'Open Fields', 'Other'];
const noClaimBonusOptions = ['12 Months  (1 Year)', '24 Months  (2 Year)', '36 Months  (3 Year)', '48 Months  (4 Year)', '60 Months  (5 Year)', '72 Months  (6 Year)', '84 Months  (7 Year)', '96 Months  (8 Year)', '108 Months  (9 Year)', '120 Months  (10 Year)'];

// Currency formatting helper functions
const formatCurrencyInput = (value: string | number): string => {
  if (!value && value !== 0) return '';
  const stringValue = typeof value === 'number' ? value.toString() : value;
  const numericValue = stringValue.replace(/[^\d.]/g, '');
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  if (decimalPart.length > 2) decimalPart = decimalPart.substring(0, 2);
  if (wholePart) wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

const cleanMaterialArray = (data: any): string[] => {
  console.log('🔍 cleanMaterialArray input:', data, 'type:', typeof data);
  
  if (!data) return [];

  // Helper to clean individual items (strips { } [ ] " and whitespace)
  const cleanItem = (item: any): string => {
    return String(item)
      .replace(/^[{\["\s]+/, '') // strip leading { [ "
      .replace(/[}\]"\s]+$/, '') // strip trailing } ] "
      .trim();
  };

  if (Array.isArray(data)) {
    return data
      .map(cleanItem)
      .filter(Boolean);
  }
  
  if (typeof data === 'string') {
    let cleanString = data.trim();
    
    // Try strict JSON parse first
    try {
      const parsed = JSON.parse(cleanString);
      if (Array.isArray(parsed)) {
        return parsed.map(cleanItem).filter(Boolean);
      }
    } catch (e) {
      // Not strict JSON, continue
    }
    
    // If it's Postgres array format {Brick,Cement} or comma-separated
    if (cleanString.startsWith('{') && cleanString.endsWith('}')) {
      cleanString = cleanString.slice(1, -1);
    } else if (cleanString.startsWith('[') && cleanString.endsWith(']')) {
      cleanString = cleanString.slice(1, -1);
    }
    
    // Split by comma and clean each piece
    return cleanString.split(',')
      .map(cleanItem)
      .filter(Boolean);
  }
  
  return [];
};

const mapAddressData = (addressData: any) => {
  return {
    addressLine1: addressData.street || '',
    addressLine2: addressData.address_line_2 || '',
    suburb: addressData.state || '',
    city: addressData.city || '',
    postalCode: addressData.zipCode || '',
    region: addressData.province || '',
    country: addressData.country || 'South Africa'
  };
};

const AddPropertyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const { quoteId } = useParams<{ quoteId: string }>();

  const clientIdFromState = location.state?.clientId;
  const clientIdFromQuery = new URLSearchParams(location.search).get('clientId');
  const clientId = clientIdFromState || clientIdFromQuery;

  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  const policyId = new URLSearchParams(location.search).get('policyId'); // ✅ ADD THIS
  
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddPropertyPage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode,
    policyId, // ✅ ADD THIS
    fromPolicy // ✅ ADD THIS
  });
  
  const returnToFromState = location.state?.returnTo;
  const returnToFromQuery = new URLSearchParams(location.search).get('returnTo');
  
  // ✅ Add debug logging
  console.log('📍 AddPropertyPage initialization:', {
    quoteId,
    clientIdFromState,
    clientIdFromQuery,
    clientId: clientId,
    returnToFromState,
    returnToFromQuery,
    returnTo: returnTo,
    fullLocation: location,
    searchParams: Object.fromEntries(new URLSearchParams(location.search))
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  
  const [propertyFormData, setPropertyFormData] = useState<Partial<Property>>({
    addressLine1: '',
    addressLine2: '',
    suburb: '',
    city: '',
    postalCode: '',
    region: '',
    country: 'South Africa',
    propertyType: '',
    residenceType: '',
    buildingAge: '',
    situatedAt: '',
    coverType: '',
    insuredValue: 0,
    hasSwimmingPool: false,
    hasMotorizedGate: false,
    isUnoccupiedMoreThan60Days: false,
    isCommune: false,
    underConstruction: false,
    builtOnSteepSlope: false,
    hasExcessiveRetainingWalls: false,
    nearWaterBody: false,
    hasRisingDamp: false,
    hasWallCracks: false,
    hasSubsidenceHistory: false,
    hasOutbuilding: false,
    hasPerimeterFence: false,
    hasElectricFence: false,
    hasThatchRoof: false,
    thatchLessThan5m: false,
    thatchSabsConductor: false,
    thatchTreated: false,
    hasAccidentalDamageCover: false,
    accidentalDamageCoverValue: 0,
    hasPowerSurgeCover: false,
    powerSurgeCoverValue: 0,
    wallMaterials: [],
    roofMaterials: [],
    floorMaterials: [],
    otherSurroundingAreas: [],
    premium: '',
    excess: 0,
    noClaimBonus: '',
  });

  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: '',
    premium: '',
    excess: '',
    powerSurgeCoverValue: '',
    accidentalDamageCoverValue: '',
  });

  const [propertyErrors, setPropertyErrors] = useState({
    addressLine1: '',
    suburb: '',
    city: '',
    postalCode: '',
    region: '',
    country: '',
    propertyType: '',
    residenceType: '',
    buildingAge: '',
    situatedAt: '',
    coverType: '',
    insuredValue: '',
    server: '',
  });

  const [addressSuggestions, setAddressSuggestions] = useState<MapboxFeature[]>([]);
const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
const autocompleteInputRef = useRef<HTMLInputElement>(null);
const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Priority: Check navigation state FIRST before API call
useEffect(() => {
  const existingData = location.state?.existingPropertyData;
  const editId = new URLSearchParams(location.search).get('editId');
  
  console.log('🔍 [PRIORITY] Checking navigation state first:', {
    hasExistingData: !!existingData,
    editId,
    existingDataKeys: existingData ? Object.keys(existingData) : []
  });
  
  if (existingData && editId) {
    console.log('✅ [PRIORITY] Using existing data from navigation state');
    
    // Use the robust parser defined at the top of the file
    const parseIfNeeded = (value: any): string[] => cleanMaterialArray(value);
    
    // ✅ Log the raw material values before parsing
    console.log('🔍 Raw material values from existingData:', {
      wallMaterials: existingData.wallMaterials || existingData.wall_materials,
      roofMaterials: existingData.roofMaterials || existingData.roof_materials,
      floorMaterials: existingData.floorMaterials || existingData.floor_materials,
      otherSurroundingAreas: existingData.otherSurroundingAreas || existingData.other_surrounding_areas,
    });
    
    const parsedWallMaterials = parseIfNeeded(existingData.wallMaterials || existingData.wall_materials);
    const parsedRoofMaterials = parseIfNeeded(existingData.roofMaterials || existingData.roof_materials);
    const parsedFloorMaterials = parseIfNeeded(existingData.floorMaterials || existingData.floor_materials);
    const parsedOtherSurroundingAreas = parseIfNeeded(existingData.otherSurroundingAreas || existingData.other_surrounding_areas);
    
    console.log('✅ Parsed material arrays:', {
      wallMaterials: parsedWallMaterials,
      roofMaterials: parsedRoofMaterials,
      floorMaterials: parsedFloorMaterials,
      otherSurroundingAreas: parsedOtherSurroundingAreas,
    });
    
    setPropertyFormData({
      ...propertyFormData,
      id: existingData.id,
      addressLine1: existingData.addressLine1 || existingData.address_line_1 || '',
      addressLine2: existingData.addressLine2 || existingData.address_line_2 || '',
      suburb: existingData.suburb || '',
      city: existingData.city || '',
      postalCode: existingData.postalCode || existingData.postal_code || '',
      region: existingData.region || '',
      country: existingData.country || 'South Africa',
      propertyType: existingData.propertyType || existingData.property_type || '',
      residenceType: existingData.residenceType || existingData.residence_type || '',
      buildingAge: existingData.buildingAge || existingData.building_age || '',
      situatedAt: existingData.situatedAt || existingData.situated_at || '',
      coverType: existingData.coverType || existingData.cover_type || '',
      insuredValue: existingData.insuredValue || existingData.insured_value || 0,
      hasSwimmingPool: existingData.hasSwimmingPool || existingData.has_swimming_pool || false,
      hasMotorizedGate: existingData.hasMotorizedGate || existingData.has_motorized_gate || false,
      isUnoccupiedMoreThan60Days: existingData.isUnoccupiedMoreThan60Days || existingData.is_unoccupied_more_than_60_days || false,
      isCommune: existingData.isCommune || existingData.is_commune || false,
      underConstruction: existingData.underConstruction || existingData.under_construction || false,
      builtOnSteepSlope: existingData.builtOnSteepSlope || existingData.built_on_steep_slope || false,
      hasExcessiveRetainingWalls: existingData.hasExcessiveRetainingWalls || existingData.has_excessive_retaining_walls || false,
      nearWaterBody: existingData.nearWaterBody || existingData.near_water_body || false,
      hasRisingDamp: existingData.hasRisingDamp || existingData.has_rising_damp || false,
      hasWallCracks: existingData.hasWallCracks || existingData.has_wall_cracks || false,
      hasSubsidenceHistory: existingData.hasSubsidenceHistory || existingData.has_subsidence_history || false,
      hasOutbuilding: existingData.hasOutbuilding || existingData.has_outbuilding || false,
      hasPerimeterFence: existingData.hasPerimeterFence || existingData.has_perimeter_fence || false,
      hasElectricFence: existingData.hasElectricFence || existingData.has_electric_fence || false,
      hasThatchRoof: existingData.hasThatchRoof || existingData.has_thatch_roof || false,
      thatchLessThan5m: existingData.thatchLessThan5m || existingData.thatch_less_than_5m || false,
      thatchSabsConductor: existingData.thatchSabsConductor || existingData.thatch_sabs_conductor || false,
      thatchTreated: existingData.thatchTreated || existingData.thatch_treated || false,
      hasAccidentalDamageCover: existingData.hasAccidentalDamageCover || existingData.has_accidental_damage_cover || false,
      accidentalDamageCoverValue: existingData.accidentalDamageCoverValue || existingData.accidental_damage_cover_value || 0,
      hasPowerSurgeCover: existingData.hasPowerSurgeCover || existingData.has_power_surge_cover || false,
      powerSurgeCoverValue: existingData.powerSurgeCoverValue || existingData.power_surge_cover_value || 0,
      wallMaterials: parsedWallMaterials,
      roofMaterials: parsedRoofMaterials,
      floorMaterials: parsedFloorMaterials,
      otherSurroundingAreas: parsedOtherSurroundingAreas,
      premium: existingData.premium || '',
      excess: existingData.excess || 0,
      noClaimBonus: existingData.noClaimBonus || existingData.no_claim_bonus || '',
    });
    
    setCurrencyDisplay({
      insuredValue: formatCurrencyInput(existingData.insuredValue || existingData.insured_value || 0),
      premium: formatCurrencyInput(existingData.premium || ''),
      excess: formatCurrencyInput(existingData.excess || 0),
      powerSurgeCoverValue: formatCurrencyInput(existingData.powerSurgeCoverValue || existingData.power_surge_cover_value || 0),
      accidentalDamageCoverValue: formatCurrencyInput(existingData.accidentalDamageCoverValue || existingData.accidental_damage_cover_value || 0),
    });
    
    setEditingId(editId);
    return; // ✅ Skip API call completely
  }
  
  // Fallback to API fetch if no existing data
  if (editId && quoteId) {
    console.log('📝 No navigation data, fetching from API:', editId);
    setEditingId(editId);
    fetchPropertyForEdit(editId);
  } else {
    console.log('ℹ️ No editId found, creating new property');
  }
}, [quoteId, location.search, location.state, policyId, fromPolicy]);

  // ✅ NEW: Debug logging for navigation parameters
useEffect(() => {
  console.log('📍 AddPropertyPage mounted with:', {
    quoteId,
    clientId,
    returnTo,
    editId: new URLSearchParams(location.search).get('editId'),
    fullUrl: window.location.href
  });
}, [quoteId, clientId, returnTo, location.search]);

// Click outside to close suggestions
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    setTimeout(() => {
      if (autocompleteInputRef.current && !autocompleteInputRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    }, 200);
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

// Cleanup debounce timer
useEffect(() => {
  return () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  };
}, []);

// Fetch address suggestions from Mapbox
const handleAddressSearch = async (searchText: string) => {
  if (!searchText || searchText.length < 3) {
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    return;
  }

  if (debounceTimerRef.current) {
    clearTimeout(debounceTimerRef.current);
  }

  debounceTimerRef.current = setTimeout(async () => {
    const token = mapboxgl.accessToken;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchText)}.json?access_token=${token}&types=address&country=ZA&limit=10`;

    try {
      console.log('🔍 Fetching Mapbox suggestions for:', searchText);
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('Mapbox API error:', response.status, response.statusText);
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        return;
      }
      
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        setAddressSuggestions(data.features);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  }, 500);
};

// Handle address selection from Mapbox suggestions
const handleAddressSelect = (feature: MapboxFeature) => {
  console.log('📍 Selected address feature:', feature);
  
  let street = feature.place_name.split(',')[0].trim();
  let city = '';
  let state = '';    // Suburb
  let province = ''; // Province
  let zipCode = '';

  feature.context?.forEach((ctx) => {
    const ctxId = ctx.id;
    
    if (ctxId.startsWith('place.')) {
      city = ctx.text;
    }
    else if (ctxId.startsWith('locality.') || ctxId.startsWith('neighborhood.')) {
      if (!state) state = ctx.text;
    }
    else if (ctxId.startsWith('region.')) {
      province = ctx.text;
    }
    else if (ctxId.startsWith('postcode.')) {
      zipCode = ctx.text;
    }
  });

  if (!state) {
    const placeParts = feature.place_name.split(',');
    if (placeParts.length >= 2) {
      const potentialSuburb = placeParts[1]?.trim();
      if (potentialSuburb && potentialSuburb !== city && !potentialSuburb.match(/^\d+$/)) {
        state = potentialSuburb;
      }
    }
  }

  if (province) {
    province = province.replace(/\d+/g, '').trim();
  }

  if (province && province.length <= 3) {
    const provinceMap: { [key: string]: string } = {
      'KZN': 'KwaZulu-Natal', 'NL': 'KwaZulu-Natal', 'EC': 'Eastern Cape',
      'WC': 'Western Cape', 'NC': 'Northern Cape', 'FS': 'Free State',
      'GT': 'Gauteng', 'LP': 'Limpopo', 'MP': 'Mpumalanga', 'NW': 'North West'
    };
    const mappedProvince = provinceMap[province.toUpperCase()];
    if (mappedProvince) province = mappedProvince;
  }

  setPropertyFormData(prev => ({
    ...prev,
    addressLine1: street,
    addressLine2: '',
    suburb: state,
    city: city,
    region: province,
    postalCode: zipCode,
    country: 'South Africa'
  }));

  setAddressSuggestions([]);
  setShowAddressSuggestions(false);
};

  const fetchPropertyForEdit = async (id: string) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/properties/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const property = response.data.data || response.data;
    
    console.log('📦 Raw property data from API for edit:', property);
    console.log('🔍 Construction materials raw values:', {
      wall_materials: property.wall_materials,
      roof_materials: property.roof_materials,
      floor_materials: property.floor_materials,
      other_surrounding_areas: property.other_surrounding_areas,
    });
    
    // Use the robust parser defined at the top of the file
    const parseMaterialsArray = (value: any): string[] => cleanMaterialArray(value);
    
    setPropertyFormData({
      id: property.id,
      addressLine1: property.address_line_1 || property.addressLine1 || '',
      addressLine2: property.address_line_2 || property.addressLine2 || '',
      suburb: property.suburb || '',
      city: property.city || '',
      postalCode: property.postal_code || property.postalCode || '',
      region: property.region || '',
      country: property.country || 'South Africa',
      propertyType: property.property_type || property.propertyType || '',
      residenceType: property.residence_type || property.residenceType || '',
      buildingAge: property.building_age || property.buildingAge || '',
      situatedAt: property.situated_at || property.situatedAt || '',
      coverType: property.cover_type || property.coverType || '',
      insuredValue: property.insured_value || property.insuredValue || 0,
      hasSwimmingPool: !!property.has_swimming_pool,
      hasMotorizedGate: !!property.has_motorized_gate,
      isUnoccupiedMoreThan60Days: !!property.is_unoccupied_more_than_60_days,
      isCommune: !!property.is_commune,
      underConstruction: !!property.under_construction,
      builtOnSteepSlope: !!property.built_on_steep_slope,
      hasExcessiveRetainingWalls: !!property.has_excessive_retaining_walls,
      nearWaterBody: !!property.near_water_body,
      hasRisingDamp: !!property.has_rising_damp,
      hasWallCracks: !!property.has_wall_cracks,
      hasSubsidenceHistory: !!property.has_subsidence_history,
      hasOutbuilding: !!property.has_outbuilding,
      hasPerimeterFence: !!property.has_perimeter_fence,
      hasElectricFence: !!property.has_electric_fence,
      hasThatchRoof: !!property.has_thatch_roof,
      thatchLessThan5m: !!property.thatch_less_than_5m,
      thatchSabsConductor: !!property.thatch_sabs_conductor,
      thatchTreated: !!property.thatch_treated,
      hasAccidentalDamageCover: !!property.has_accidental_damage_cover,
      accidentalDamageCoverValue: property.accidental_damage_cover_value || 0,
      hasPowerSurgeCover: !!property.has_power_surge_cover,
      powerSurgeCoverValue: property.power_surge_cover_value || 0,
      // ✅ Use the improved parser
      wallMaterials: parseMaterialsArray(property.wall_materials),
      roofMaterials: parseMaterialsArray(property.roof_materials),
      floorMaterials: parseMaterialsArray(property.floor_materials),
      otherSurroundingAreas: parseMaterialsArray(property.other_surrounding_areas),
      premium: property.premium || '',
      excess: property.excess || 0,
      noClaimBonus: property.no_claim_bonus || '',
    });
    
    console.log('✅ Parsed form data:', {
      wallMaterials: parseMaterialsArray(property.wall_materials),
      roofMaterials: parseMaterialsArray(property.roof_materials),
      floorMaterials: parseMaterialsArray(property.floor_materials),
      otherSurroundingAreas: parseMaterialsArray(property.other_surrounding_areas),
    });
    
    setCurrencyDisplay({
      insuredValue: formatCurrencyInput(property.insured_value || 0),
      premium: formatCurrencyInput(property.premium || ''),
      excess: formatCurrencyInput(property.excess || 0),
      powerSurgeCoverValue: formatCurrencyInput(property.power_surge_cover_value || 0),
      accidentalDamageCoverValue: formatCurrencyInput(property.accidental_damage_cover_value || 0),
    });
    
  } catch (error) {
    console.error('Error fetching property for edit:', error);
    setSaveMessage('❌ Failed to load property data');
  }
};

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    
    if (isCheckbox) {
      const checked = (e.target as HTMLInputElement).checked;
      setPropertyFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setPropertyFormData(prev => ({ ...prev, [name]: value }));
      setPropertyErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'insuredValue' | 'premium' | 'excess') => {
    const { value } = e.target;
    const formattedValue = formatCurrencyInput(value);
    const numericValue = parseCurrencyInput(value);
    
    setPropertyFormData(prev => ({ ...prev, [fieldName]: numericValue }));
    setCurrencyDisplay(prev => ({ ...prev, [fieldName]: formattedValue }));
    setPropertyErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const handleCheckboxGroupChange = (
    field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingAreas',
    option: string,
    isChecked: boolean
  ) => {
    setPropertyFormData(prev => {
      const currentArray = (prev[field] as string[]) || [];
      if (isChecked && !currentArray.includes(option)) {
        return { ...prev, [field]: [...currentArray, option] };
      } else if (!isChecked && currentArray.includes(option)) {
        return { ...prev, [field]: currentArray.filter(item => item !== option) };
      }
      return prev;
    });
  };

  const clearAllSelections = (field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingAreas') => {
    setPropertyFormData(prev => ({ ...prev, [field]: [] }));
  };

  const fetchClientAddress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/addresses/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const addressData = await response.json();
        const mappedData = mapAddressData(addressData);
        setPropertyFormData(prev => ({ ...prev, ...mappedData }));
        setSaveMessage('✓ Address filled successfully from client data!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      setSaveMessage('❌ Failed to fetch address');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const validateForm = () => {
    const newErrors = {
      addressLine1: !propertyFormData.addressLine1 ? 'Address Line 1 is required' : '',
      suburb: !propertyFormData.suburb ? 'Suburb is required' : '',
      city: !propertyFormData.city ? 'City/Town is required' : '',
      postalCode: !propertyFormData.postalCode ? 'Postal Code is required' : '',
      region: !propertyFormData.region ? 'Region is required' : '',
      country: !propertyFormData.country ? 'Country is required' : '',
      propertyType: !propertyFormData.propertyType ? 'Property Type is required' : '',
      residenceType: !propertyFormData.residenceType ? 'Residence Type is required' : '',
      buildingAge: !propertyFormData.buildingAge ? 'Building Age is required' : '',
      situatedAt: !propertyFormData.situatedAt ? 'Situated At is required' : '',
      coverType: !propertyFormData.coverType ? 'Cover Type is required' : '',
      insuredValue: !propertyFormData.insuredValue ? 'Insured Value is required' : '',
      server: '',
    };
    setPropertyErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSubmitting) return;
  if (!validateForm()) return;
  
  setIsSubmitting(true);
  setSaveMessage('Adding property...');

  // Convert arrays to JSON strings for backend storage - ✅ USE JSON.stringify for proper array storage
const propertyToSave: any = {
  ...propertyFormData,
  id: propertyFormData.id || `temp-property-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
  insuredValue: propertyFormData.insuredValue || 0,
  premium: propertyFormData.premium || '',
  excess: propertyFormData.excess || 0,
  // ✅ CRITICAL FIX: Use JSON.stringify for proper array storage
  wallMaterials: JSON.stringify(propertyFormData.wallMaterials || []),
  roofMaterials: JSON.stringify(propertyFormData.roofMaterials || []),
  floorMaterials: JSON.stringify(propertyFormData.floorMaterials || []),
  otherSurroundingAreas: JSON.stringify(propertyFormData.otherSurroundingAreas || []),
  hasThatchRoof: propertyFormData.hasThatchRoof === true,
  thatch_less_than_5m: propertyFormData.thatchLessThan5m === true,
  thatch_sabs_conductor: propertyFormData.thatchSabsConductor === true,
  thatch_treated: propertyFormData.thatchTreated === true,
  has_power_surge_cover: propertyFormData.hasPowerSurgeCover === true,
  power_surge_cover_value: propertyFormData.powerSurgeCoverValue || 0,
  has_accidental_damage_cover: propertyFormData.hasAccidentalDamageCover === true,
  accidental_damage_cover_value: propertyFormData.accidentalDamageCoverValue || 0,
  has_swimming_pool: propertyFormData.hasSwimmingPool === true,
  has_motorized_gate: propertyFormData.hasMotorizedGate === true,
  is_unoccupied_more_than_60_days: propertyFormData.isUnoccupiedMoreThan60Days === true,
  is_commune: propertyFormData.isCommune === true,
  under_construction: propertyFormData.underConstruction === true,
  built_on_steep_slope: propertyFormData.builtOnSteepSlope === true,
  has_excessive_retaining_walls: propertyFormData.hasExcessiveRetainingWalls === true,
  near_water_body: propertyFormData.nearWaterBody === true,
  has_rising_damp: propertyFormData.hasRisingDamp === true,
  has_wall_cracks: propertyFormData.hasWallCracks === true,
  has_subsidence_history: propertyFormData.hasSubsidenceHistory === true,
  has_outbuilding: propertyFormData.hasOutbuilding === true,
  has_perimeter_fence: propertyFormData.hasPerimeterFence === true,
  has_electric_fence: propertyFormData.hasElectricFence === true,
  address_line_1: propertyFormData.addressLine1,
  address_line_2: propertyFormData.addressLine2,
  postal_code: propertyFormData.postalCode,
  property_type: propertyFormData.propertyType,
  residence_type: propertyFormData.residenceType,
  building_age: propertyFormData.buildingAge,
  situated_at: propertyFormData.situatedAt,
  cover_type: propertyFormData.coverType,
  insured_value: propertyFormData.insuredValue || 0,
  no_claim_bonus: propertyFormData.noClaimBonus || '',
  isEditing: false,
};

// ✅ ADD DEBUG LOGGING TO CONFIRM ARRAYS ARE BEING SAVED
console.log('📤 Saving property with arrays as JSON strings:', {
  wallMaterials: propertyToSave.wallMaterials,
  roofMaterials: propertyToSave.roofMaterials,
  floorMaterials: propertyToSave.floorMaterials,
  otherSurroundingAreas: propertyToSave.otherSurroundingAreas,
});
console.log('📤 Form data wallMaterials:', propertyFormData.wallMaterials);
console.log('📤 Form data roofMaterials:', propertyFormData.roofMaterials);
console.log('📤 Form data floorMaterials:', propertyFormData.floorMaterials);
console.log('📤 Form data otherSurroundingAreas:', propertyFormData.otherSurroundingAreas);

  try {
    const token = localStorage.getItem('token');
    
    // Clean the quoteId - convert "null" string or invalid values to actual null
let cleanQuoteId: number | null = null;
if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
  cleanQuoteId = parseInt(quoteId);
}
    
    const propertyWithClientId = {
      ...propertyToSave,
      client_id: parseInt(clientId),
      quote_id: cleanQuoteId
    };
    
    // If no quote_id, save to localStorage (draft mode)
if (!cleanQuoteId) {
  console.log('💾 No quote ID - saving property to localStorage');
  
  // ✅ Determine the storage key based on context
  let storageKey: string;
  
  // ✅ Only use policy key if we're explicitly coming from policy
  if (policyId && fromPolicy === true) {
    // Coming from Policy Detail page - save with policy ID key
    storageKey = `policy_${policyId}_property`;
    console.log(`📂 Saving property with policy key: ${storageKey}`);
  } else {
    // Coming from Quote/Create Quote page - save with client draft key
    storageKey = `client_${clientId}_properties_draft`;
    console.log(`📂 Saving property with client draft key: ${storageKey}`);
  }
  
  const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  // ✅ Ensure arrays are saved as JSON strings in localStorage
  const draftToSave = {
    ...propertyWithClientId,
    // ✅ Use JSON.stringify for arrays in localStorage as well
    wallMaterials: JSON.stringify(propertyFormData.wallMaterials || []),
    roofMaterials: JSON.stringify(propertyFormData.roofMaterials || []),
    floorMaterials: JSON.stringify(propertyFormData.floorMaterials || []),
    otherSurroundingAreas: JSON.stringify(propertyFormData.otherSurroundingAreas || []),
  };
  
  // Check if we're editing an existing draft
  let updatedDrafts;
  if (editingId && editingId.startsWith('temp-')) {
    // Update existing draft
    updatedDrafts = existingDrafts.map((item: any) => 
      item.id === editingId 
        ? { 
            ...item, 
            ...draftToSave, 
            id: editingId,
            isTemp: true,
            status: 'active',
            inception_date: new Date().toISOString().split('T')[0],
            product_type: 'properties'
          }
        : item
    );
  } else {
    // Add new draft
    updatedDrafts = [
      ...existingDrafts,
      {
        ...draftToSave,
        id: `temp-${Date.now()}`,
        isTemp: true,
        status: 'active',
        inception_date: new Date().toISOString().split('T')[0],
        product_type: 'properties'
      }
    ];
  }
  
  localStorage.setItem(storageKey, JSON.stringify(updatedDrafts));
  
  setSaveMessage('Property saved to draft!');
  
  // ✅ Navigate based on context
  setTimeout(() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${clientId}`;
      console.log('📍 Returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Master quote mode - navigate back to master quote page
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${clientId}`;
      console.log('📍 Returning to master quote page:', finalUrl);
      navigate(finalUrl);
    } else {
      // Create quote mode
      navigate(`/create-quote/${clientId}`, { 
        state: { refreshProducts: true, hasDrafts: true }
      });
    }
  }, 1500);

    } else {
      // Has quote_id - save to database
      if (editingId) {
  await axios.put(`${API_URL}/api/properties/${editingId}`, propertyWithClientId, {
    headers: { Authorization: `Bearer ${token}` }
  });
  setSaveMessage('Property updated successfully!');
  
  // ✅ LOG ACTIVITY: Property Updated
  if (cleanQuoteId) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const performedBy = user?.username || user?.email || 'System';
      
      await axios.post(
        `${API_URL}/api/quote-activity-logs`,
        {
          quote_id: cleanQuoteId,
          action_type: 'product_updated',
          description: `Property updated: ${propertyFormData.propertyType || 'Property'}`,
          metadata: {
            productType: 'property',
            productId: editingId,
            performedBy: performedBy,
            updatedFields: Object.keys(propertyFormData).filter(key => 
              key !== 'id' && key !== 'isEditing' && key !== 'isTemp'
            ),
            address: `${propertyFormData.addressLine1 || ''} ${propertyFormData.suburb || ''}`.trim() || 'No address',
            propertyType: propertyFormData.propertyType || 'Not specified',
            insuredValue: propertyFormData.insuredValue || 0,
            wallMaterials: propertyFormData.wallMaterials || [],
            roofMaterials: propertyFormData.roofMaterials || [],
            floorMaterials: propertyFormData.floorMaterials || [],
            otherSurroundingAreas: propertyFormData.otherSurroundingAreas || [],
            updatedAt: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Activity logged: property_updated');
    } catch (logError) {
      console.error('❌ Failed to log property update:', logError);
      // Don't fail the update if logging fails
    }
  }
} else {
  await axios.post(`${API_URL}/api/properties`, propertyWithClientId, {
    headers: { Authorization: `Bearer ${token}` }
  });
  setSaveMessage('Property added successfully!');
  
  // ✅ LOG ACTIVITY: Property Added
  if (cleanQuoteId) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const performedBy = user?.username || user?.email || 'System';
      
      await axios.post(
        `${API_URL}/api/quote-activity-logs`,
        {
          quote_id: cleanQuoteId,
          action_type: 'product_added',
          description: `Property added: ${propertyFormData.propertyType || 'Property'}`,
          metadata: {
            productType: 'property',
            performedBy: performedBy,
            address: `${propertyFormData.addressLine1 || ''} ${propertyFormData.suburb || ''}`.trim() || 'No address',
            propertyType: propertyFormData.propertyType || 'Not specified',
            insuredValue: propertyFormData.insuredValue || 0,
            wallMaterials: propertyFormData.wallMaterials || [],
            roofMaterials: propertyFormData.roofMaterials || [],
            floorMaterials: propertyFormData.floorMaterials || [],
            otherSurroundingAreas: propertyFormData.otherSurroundingAreas || [],
            addedAt: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Activity logged: property_added');
    } catch (logError) {
      console.error('❌ Failed to log property addition:', logError);
      // Don't fail the addition if logging fails
    }
  }
}
      
      setTimeout(() => {
  // ✅ FIX: Always use clientId from state, ensure it's valid
  const validClientId: string | null = clientId || new URLSearchParams(location.search).get('clientId');
  
  if (!validClientId) {
    console.error('❌ No clientId available for navigation');
    setSaveMessage('Error: Missing client ID');
    setIsSubmitting(false);
    return;
  }
  
  // Prioritize returnTo URL (which should be the master quote page)
  if (returnTo) {
  const decodedReturnUrl = decodeURIComponent(returnTo);
  console.log('📍 Returning to:', decodedReturnUrl);
  
  // Check if returnUrl already has query params
  const separator = decodedReturnUrl.includes('?') ? '&' : '?';
  const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${validClientId}`;
  console.log('📍 Final navigation URL:', finalUrl);
  navigate(finalUrl);
} else if (cleanQuoteId !== null && cleanQuoteId !== undefined) {
  // Fallback: navigate to master quote page with the quote ID
  const masterQuoteUrl = `/quotes/master/${validClientId}/${cleanQuoteId}`;
  const finalUrl = `${masterQuoteUrl}?refreshProducts=true&productAdded=property`;
  console.log('📍 Fallback navigation URL:', finalUrl);
  navigate(finalUrl);
} else {
  console.error('❌ No quoteId available for navigation');
  navigate(`/personal/${validClientId}`, { state: { activeTab: "quotes" } });
}
}, 1500);
    }
    
  } catch (error: any) {
    console.error('Error saving property:', error);
    setSaveMessage(error.response?.data?.message || 'Failed to save property');
    setIsSubmitting(false);
  }
};

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Property Details' : 'Add Property'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-2xl mx-auto">

            <button
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Go back to create quote page — preserve drafts (do not trigger fresh reset)
      navigate(`/create-quote/${clientId}`, { state: { fromCancel: true } });
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="relative bg-white p-6 rounded-lg shadow-xl w-full">
              <h3 className="text-xl font-bold mb-4">
                {editingId ? 'Edit Property Details' : 'Add Property'}
              </h3>
              
              {saveMessage && (
                <div className={`${saveMessage.includes('❌') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} border px-4 py-3 rounded relative mb-4`}>
                  {saveMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="space-y-4 mb-6">
                  {/* Fill from Client Button */}
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={fetchClientAddress}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
                    >
                      Fill from Client
                    </button>
                  </div>

                  {/* Address Line 1 and 2 */}
                  <div className="flex space-x-4">

                    <div className="flex-1 relative">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Address Line 1 *</label>
                      <input
                        type="text"
                        name="addressLine1"
                        ref={autocompleteInputRef}
                        value={propertyFormData.addressLine1}
                        onChange={(e) => {
                          handleInputChange(e);
                          handleAddressSearch(e.target.value);
                        }}
                        onFocus={() => {
                          if (propertyFormData.addressLine1 && propertyFormData.addressLine1.length >= 3) {
                            handleAddressSearch(propertyFormData.addressLine1);
                          }
                        }}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="Start typing address..."
                        autoComplete="off"
                      />
                      {propertyErrors.addressLine1 && <p className="text-red-500 text-xs mt-1">{propertyErrors.addressLine1}</p>}
                      
                      {/* Address Suggestions Dropdown */}
                      {showAddressSuggestions && addressSuggestions.length > 0 && (
                        <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                          {addressSuggestions.map((suggestion) => (
                            <li
                              key={suggestion.id}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddressSelect(suggestion);
                              }}
                            >
                              {suggestion.place_name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Address Line 2</label>
                      <input
                        type="text"
                        name="addressLine2"
                        value={propertyFormData.addressLine2}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      />
                    </div>
                  </div>

                  {/* Suburb and City */}
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Suburb *</label>
                      <input
                        type="text"
                        name="suburb"
                        value={propertyFormData.suburb}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      />
                      {propertyErrors.suburb && <p className="text-red-500 text-xs mt-1">{propertyErrors.suburb}</p>}
                    </div>
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">City/Town *</label>
                      <input
                        type="text"
                        name="city"
                        value={propertyFormData.city}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      />
                      {propertyErrors.city && <p className="text-red-500 text-xs mt-1">{propertyErrors.city}</p>}
                    </div>
                  </div>

                  {/* Postal Code and Region */}
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Postal Code *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={propertyFormData.postalCode}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      />
                      {propertyErrors.postalCode && <p className="text-red-500 text-xs mt-1">{propertyErrors.postalCode}</p>}
                    </div>
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Region *</label>
                      <input
                        type="text"
                        name="region"
                        value={propertyFormData.region}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      />
                      {propertyErrors.region && <p className="text-red-500 text-xs mt-1">{propertyErrors.region}</p>}
                    </div>
                  </div>

                  {/* Country and Property Type */}
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Country *</label>
                      <input
                        type="text"
                        name="country"
                        value={propertyFormData.country}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      />
                      {propertyErrors.country && <p className="text-red-500 text-xs mt-1">{propertyErrors.country}</p>}
                    </div>
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Property Type *</label>
                      <select
                        name="propertyType"
                        value={propertyFormData.propertyType}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      >
                        <option value="" disabled>Select property type</option>
                        {propertyTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                      {propertyErrors.propertyType && <p className="text-red-500 text-xs mt-1">{propertyErrors.propertyType}</p>}
                    </div>
                  </div>

                  {/* Residence Type and Building Age */}
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Residence Type *</label>
                      <select
                        name="residenceType"
                        value={propertyFormData.residenceType}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      >
                        <option value="" disabled>Select residence type</option>
                        {residenceTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                      {propertyErrors.residenceType && <p className="text-red-500 text-xs mt-1">{propertyErrors.residenceType}</p>}
                    </div>
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Building Age</label>
                      <select
                        name="buildingAge"
                        value={propertyFormData.buildingAge}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      >
                        <option value="" disabled>Select building age</option>
                        {buildingAgeOptions.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                      {propertyErrors.buildingAge && <p className="text-red-500 text-xs mt-1">{propertyErrors.buildingAge}</p>}
                    </div>
                  </div>

                  {/* Situated At and Cover Type */}
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Situated At</label>
                      <select
                        name="situatedAt"
                        value={propertyFormData.situatedAt}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      >
                        <option value="" disabled>Select location type</option>
                        {situatedAtOptions.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                      {propertyErrors.situatedAt && <p className="text-red-500 text-xs mt-1">{propertyErrors.situatedAt}</p>}
                    </div>
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">Cover Type *</label>
                      <select
                        name="coverType"
                        value={propertyFormData.coverType}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      >
                        <option value="" disabled>Select cover type</option>
                        {coverTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                      {propertyErrors.coverType && <p className="text-red-500 text-xs mt-1">{propertyErrors.coverType}</p>}
                    </div>
                  </div>

                  {/* Insured Value and No Claim Bonus */}
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">
                        Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500">R</span>
                        </div>
                        <input
                          type="text"
                          name="insuredValue"
                          value={currencyDisplay.insuredValue}
                          onChange={(e) => handleCurrencyChange(e, 'insuredValue')}
                          onBlur={() => handleCurrencyBlur('insuredValue')}
                          className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                      {propertyErrors.insuredValue && <p className="text-red-500 text-xs mt-1">{propertyErrors.insuredValue}</p>}
                    </div>
                    <div className="flex-1">
                      <label className="block text-gray-700 font-bold mb-2 text-sm">No Claim Bonus</label>
                      <select
                        name="noClaimBonus"
                        value={propertyFormData.noClaimBonus || ''}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      >
                        <option value="" disabled>Select no claim bonus</option>
                        {noClaimBonusOptions.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Additional Property Details Checkboxes */}
                  <div className="mt-4">
                    <h4 className="text-gray-700 font-bold mb-2 text-sm">Additional Property Details</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasSwimmingPool" checked={propertyFormData.hasSwimmingPool} onChange={handleInputChange} className="mr-2" />
                        Has swimming pool
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasMotorizedGate" checked={propertyFormData.hasMotorizedGate} onChange={handleInputChange} className="mr-2" />
                        Has motorized gate
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="isUnoccupiedMoreThan60Days" checked={propertyFormData.isUnoccupiedMoreThan60Days} onChange={handleInputChange} className="mr-2" />
                        Is the property unoccupied for more than 60 consecutive days per annum
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="isCommune" checked={propertyFormData.isCommune} onChange={handleInputChange} className="mr-2" />
                        Is commune
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="underConstruction" checked={propertyFormData.underConstruction} onChange={handleInputChange} className="mr-2" />
                        Under construction
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="builtOnSteepSlope" checked={propertyFormData.builtOnSteepSlope} onChange={handleInputChange} className="mr-2" />
                        Built on steep slope
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasExcessiveRetainingWalls" checked={propertyFormData.hasExcessiveRetainingWalls} onChange={handleInputChange} className="mr-2" />
                        Has excessive use of retaining walls
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="nearWaterBody" checked={propertyFormData.nearWaterBody} onChange={handleInputChange} className="mr-2" />
                        Less than 100 m from Sea, Lagoon, River, Natural Stream, Dam or Swamp
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasRisingDamp" checked={propertyFormData.hasRisingDamp} onChange={handleInputChange} className="mr-2" />
                        Has visible signs of rising damp
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasWallCracks" checked={propertyFormData.hasWallCracks} onChange={handleInputChange} className="mr-2" />
                        Has visible signs of cracks in the wall
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasSubsidenceHistory" checked={propertyFormData.hasSubsidenceHistory} onChange={handleInputChange} className="mr-2" />
                        Has history of subsidence or landslip in the area
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasOutbuilding" checked={propertyFormData.hasOutbuilding} onChange={handleInputChange} className="mr-2" />
                        Has outbuilding
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasPerimeterFence" checked={propertyFormData.hasPerimeterFence} onChange={handleInputChange} className="mr-2" />
                        Property surrounded by perimeter fence at least 1.8m high
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" name="hasElectricFence" checked={propertyFormData.hasElectricFence} onChange={handleInputChange} className="mr-2" />
                        Has electric fence on perimeter
                      </label>
                    </div>
                  </div>

                  {/* Thatch Roof Section */}
                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input type="checkbox" name="hasThatchRoof" checked={propertyFormData.hasThatchRoof} onChange={handleInputChange} className="mr-2" />
                      Thatch roof present
                    </label>
                    {propertyFormData.hasThatchRoof && (
                      <div className="ml-6 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md space-y-2">
                        <p className="text-xs font-medium text-blue-800 mb-2">Additional Thatch Roof Details:</p>
                        <label className="flex items-center text-xs">
                          <input type="checkbox" name="thatchLessThan5m" checked={propertyFormData.thatchLessThan5m} onChange={handleInputChange} className="mr-2" />
                          Less than 5m from main dwelling
                        </label>
                        <label className="flex items-center text-xs">
                          <input type="checkbox" name="thatchSabsConductor" checked={propertyFormData.thatchSabsConductor} onChange={handleInputChange} className="mr-2" />
                          SABS lightning conductor present
                        </label>
                        <label className="flex items-center text-xs">
                          <input type="checkbox" name="thatchTreated" checked={propertyFormData.thatchTreated} onChange={handleInputChange} className="mr-2" />
                          Thatch treated
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Accidental Damage Cover */}
                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input type="checkbox" name="hasAccidentalDamageCover" checked={propertyFormData.hasAccidentalDamageCover} onChange={handleInputChange} className="mr-2" />
                      Accidental damage cover
                    </label>
                    {propertyFormData.hasAccidentalDamageCover && (
                      <div className="ml-6 mt-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Accidental Damage Cover Value</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">R</span>
                          </div>
                          <input
  type="text"
  value={currencyDisplay.accidentalDamageCoverValue}
  onChange={(e) => {
    const formattedValue = formatCurrencyInput(e.target.value);
    const numericValue = parseCurrencyInput(e.target.value);
    setPropertyFormData(prev => ({ ...prev, accidentalDamageCoverValue: numericValue }));
    setCurrencyDisplay(prev => ({ ...prev, accidentalDamageCoverValue: formattedValue }));
  }}
  onBlur={() => {
    const currentValue = currencyDisplay.accidentalDamageCoverValue;
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        // ✅ Format to always have exactly 2 decimal places
        const formatted = numericValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        setCurrencyDisplay(prev => ({ ...prev, accidentalDamageCoverValue: formatted }));
      }
    }
  }}
  className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
  placeholder="0.00"
/>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Power Surge Cover */}
                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input type="checkbox" name="hasPowerSurgeCover" checked={propertyFormData.hasPowerSurgeCover} onChange={handleInputChange} className="mr-2" />
                      Power surge cover
                    </label>
                    {propertyFormData.hasPowerSurgeCover && (
                      <div className="ml-6 mt-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Power Surge Cover Value</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">R</span>
                          </div>
                          <input
  type="text"
  value={currencyDisplay.powerSurgeCoverValue}
  onChange={(e) => {
    const formattedValue = formatCurrencyInput(e.target.value);
    const numericValue = parseCurrencyInput(e.target.value);
    setPropertyFormData(prev => ({ ...prev, powerSurgeCoverValue: numericValue }));
    setCurrencyDisplay(prev => ({ ...prev, powerSurgeCoverValue: formattedValue }));
  }}
  onBlur={() => {
    const currentValue = currencyDisplay.powerSurgeCoverValue;
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        // ✅ Format to always have exactly 2 decimal places
        const formatted = numericValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        setCurrencyDisplay(prev => ({ ...prev, powerSurgeCoverValue: formatted }));
      }
    }
  }}
  className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
  placeholder="0.00"
/>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Construction and Surroundings */}
                  <div className="mt-4">
                    <h4 className="text-gray-700 font-bold mb-2 text-sm">Construction and Surroundings</h4>
                    <div className="space-y-6">
                      
                      {/* Wall Materials */}
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Wall Materials</label>
                        <div className="grid grid-cols-2 gap-2">
                          {wallMaterialsOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={(propertyFormData.wallMaterials || []).includes(option)}
                                onChange={(e) => handleCheckboxGroupChange('wallMaterials', option, e.target.checked)}
                                className="mr-2"
                              />
                              {option}
                            </label>
                          ))}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          {(propertyFormData.wallMaterials && propertyFormData.wallMaterials.length > 0) && (
                            <p className="text-xs text-green-600">Selected: {propertyFormData.wallMaterials.join(', ')}</p>
                          )}
                          <button
                            type="button"
                            onClick={() => clearAllSelections('wallMaterials')}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                      {/* Roof Materials */}
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Roof Materials</label>
                        <div className="grid grid-cols-2 gap-2">
                          {roofMaterialsOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={(propertyFormData.roofMaterials || []).includes(option)}
                                onChange={(e) => handleCheckboxGroupChange('roofMaterials', option, e.target.checked)}
                                className="mr-2"
                              />
                              {option}
                            </label>
                          ))}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          {(propertyFormData.roofMaterials && propertyFormData.roofMaterials.length > 0) && (
                            <p className="text-xs text-green-600">Selected: {propertyFormData.roofMaterials.join(', ')}</p>
                          )}
                          <button
                            type="button"
                            onClick={() => clearAllSelections('roofMaterials')}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                      {/* Floor Materials */}
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Floor Materials</label>
                        <div className="grid grid-cols-2 gap-2">
                          {floorMaterialsOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={(propertyFormData.floorMaterials || []).includes(option)}
                                onChange={(e) => handleCheckboxGroupChange('floorMaterials', option, e.target.checked)}
                                className="mr-2"
                              />
                              {option}
                            </label>
                          ))}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          {(propertyFormData.floorMaterials && propertyFormData.floorMaterials.length > 0) && (
                            <p className="text-xs text-green-600">Selected: {propertyFormData.floorMaterials.join(', ')}</p>
                          )}
                          <button
                            type="button"
                            onClick={() => clearAllSelections('floorMaterials')}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                      {/* Other Surrounding Areas */}
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Other Surrounding Areas</label>
                        <div className="grid grid-cols-2 gap-2">
                          {surroundingAreasOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={(propertyFormData.otherSurroundingAreas || []).includes(option)}
                                onChange={(e) => handleCheckboxGroupChange('otherSurroundingAreas', option, e.target.checked)}
                                className="mr-2"
                              />
                              {option}
                            </label>
                          ))}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          {(propertyFormData.otherSurroundingAreas && propertyFormData.otherSurroundingAreas.length > 0) && (
                            <p className="text-xs text-green-600">{propertyFormData.otherSurroundingAreas.length} areas selected</p>
                          )}
                          <button
                            type="button"
                            onClick={() => clearAllSelections('otherSurroundingAreas')}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Insurance Details */}
                  <div className="mt-4">
                    <h4 className="text-gray-700 font-bold mb-2 text-sm">Insurance Details</h4>
                    <div className="flex space-x-4">
                      <div className="flex-1">
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Premium</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.premium}
                            onChange={(e) => handleCurrencyChange(e, 'premium')}
                            onBlur={() => handleCurrencyBlur('premium')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-gray-700 font-bold mb-2 text-sm">
                          Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.excess}
                            onChange={(e) => handleCurrencyChange(e, 'excess')}
                            onBlur={() => handleCurrencyBlur('excess')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-4">

                  <button
  type="button"
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=property&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Preserve drafts on the create-quote page — do not trigger fresh reset
      navigate(`/create-quote/${clientId}`, { state: { fromCancel: true } });
    }
  }}
  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
>
  Cancel
</button>

                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : (editingId ? 'Update Property' : 'Submit Property')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyPage;